'use client';

import { logger } from '@/lib/logger-client';
import React, { useState, useEffect, useRef, useCallback } from "react";
import { VideoObject } from "@/interfaces";
import VideoCardLTR from "@/components/videoCard/index";
import VideoCardRTL from "@/components/videoCard/rtl/index";
import { useQueryState } from "nuqs";
import { fetchVideosWithPagination } from "@/lib/api/videos";
import { useAuth } from "@/components/AuthProvider/authContext";
import { useLanguage } from "@/hooks/useLanguage";

const VideosPage: React.FC = () => {
  const { t, isRTL } = useLanguage();
  const { user: _user } = useAuth();
  const [videos, setVideos] = useState<VideoObject[]>([]);
  const [_cursor, setCursor] = useQueryState("cursor", { shallow: true });
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isCollapsed, _setIsCollapsed] = useState(true);

  // Select VideoCard component based on locale
  const VideoCard = isRTL ? VideoCardRTL : VideoCardLTR;

  // Prevent duplicate initial fetch
  const hasInitialFetch = useRef(false);

  const fetchVideos = useCallback(async (cursor: string | null) => {
    setLoading(true);
    try {
      const data = await fetchVideosWithPagination(cursor);

      if (!data) {
        logger.error("Failed to fetch videos");
        return;
      }

      // Append new videos to the existing list, avoiding duplicates
      setVideos((prevVideos) => {
        const existingIds = new Set(prevVideos.map(video => video.id));
        const newVideos = data.data.filter(video => !existingIds.has(video.id));
        return [...prevVideos, ...newVideos];
      });
      setNextCursor(data.pagination?.nextCursor ?? null); // Update the next cursor
    } catch (error) {
      logger.error("Error fetching videos:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Prevent duplicate initial fetch
    if (hasInitialFetch.current) {
      logger.debug('VideosPage: Skipping duplicate initial fetch');
      return;
    }

    logger.debug('VideosPage: Performing initial fetch');
    hasInitialFetch.current = true;
    fetchVideos(null);
  }, [fetchVideos]);

  const handleNextPage = () => {
    if (nextCursor) {
      setCursor(nextCursor); // Update the cursor in the query state
      fetchVideos(nextCursor); // Fetch the next page of videos
    }
  };

  return (
        <main
          className={`flex flex-col p-4 w-full transition-all duration-300 ${
            isCollapsed ? (isRTL ? "md:pr-20" : "md:pl-20") : (isRTL ? "md:pr-56" : "md:pl-56")
          }`}
          style={{ direction: isRTL ? "rtl" : "ltr" }}
        >
<h1 className="text-2xl font-bold text-black dark:text-white mb-4">{t.video.videos}</h1>
          {loading && videos.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400">{t.common.loading}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-5 gap-4">
              {videos.map((video) => (
                <VideoCard key={video.id} {...video} />
              ))}
            </div>
          )}
          <div className="flex justify-center items-center mt-6">
            <button
              onClick={handleNextPage}
              disabled={!nextCursor || loading}
              className="px-4 py-2 bg-gray-700 text-white rounded disabled:opacity-50"
            >
              {loading ? t.common.loading : t.common.viewAll}
            </button>
          </div>
        </main>
  );
};

export default VideosPage;
